import { isStaleFix } from '../models/located.model';
import { LocatedVesselEntry, LocatedVesselRecord } from '../contracts/vessel.types';
import { AisMapContext } from '../contracts/ais-map.context';

/**
 * The one act this module must reach a SIBLING for (rule R2 — no sibling
 * imports in map-core): taking a marker off the located LAYER, which owns the
 * Leaflet group. The facade wires this to `LocatedLayer.removeMarker`.
 */
export interface LocatedStoreOptions {
  removeMarker: (marker: any) => void;

  // ── Persistence (decision D8) ───────────────────────────────────────────────
  // Saving a hull against a task+sensor document is a PROVIDER arrangement, so
  // this module never learns one: it keeps the record shape, `kept`, and the
  // panel-row push, and is TOLD the three acts — the same way
  // `TargetController` is told `ownScene`. See `vr-saved-vessels.gateway.ts`.
  /** True when the hull has a saved record on this provider (drawn or not). */
  isSavedRecord: (mmsi: string) => boolean;
  /**
   * Persist; resolves to the kept-ness this store should record, or NULL when
   * nothing was attempted (no task/sensor to file against) — a record that was
   * never offered to the provider must not be marked unsaved.
   */
  persist: (record: LocatedVesselRecord) => Promise<boolean | null>;
  /** Forget it — `wasKept` is this store's verdict on whether it was saved. */
  forget: (mmsi: string, wasKept: boolean) => void;
}

/**
 * LOCATED VESSELS — the STORE half: the records, their keep/remove lifecycle
 * against the server, and the rows the Saved Vessels panel renders. Extracted
 * verbatim from `VoyageMapController` (phase 4, REFACTOR-PLAN.md §7). The
 * PAINTING of these vessels — markers, twins, labels — is the layer half
 * (`located.layer.ts`); the two share the entry map declared here and reach
 * each other only through the facade's wiring.
 *
 * NO PROVIDER KNOWLEDGE (decision D8): persistence arrived here as direct
 * `saveVoyageRadarVessel` / `removeVoyageRadarVessel` calls and moved out to
 * `ais/voyage-radar/gateways/vr-saved-vessels.gateway.ts`. What is left is
 * genuinely shared — the record shape, the `kept` bookkeeping, and re-pushing
 * the panel rows — and the three acts arrive as injected callbacks.
 */
export class LocatedStore {
  /**
   * mmsi → { record, marker, ring } for every located vessel ON the map.
   *
   * `ring` records which of the two forms the marker is currently wearing — the
   * full amber diamond, or the ring-only form it takes when the snapshot is
   * already drawing this hull at the same fix. See `syncLocatedTwins`.
   *
   * PUBLIC: the layer half paints out of this map, and the facade's remaining
   * feature code (pickers, search, lookup) reads it until phase 5 moves them.
   */
  readonly vessels = new Map<string, LocatedVesselEntry>();

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: LocatedStoreOptions,
  ) {}

  /** True when a history vessel's marker is currently drawn — the panel's "on map" tag. */
  isLocatedOnMap(mmsi: string): boolean {
    return this.vessels.has(String(mmsi));
  }

  /** True when the vessel has a saved record (drawn or not). */
  isSaved(mmsi: string): boolean {
    return this.opts.isSavedRecord(mmsi);
  }

  /**
   * The record for a located hull, or null when it is not on the map.
   *
   * The read half of the entry map, added in phase 9 so consumers ask this
   * module for a record instead of walking `vessels` themselves. The map stays
   * public because the layer paints out of it, but a reader that only wants
   * one record should not have to know it is a Map, or that entries carry a
   * marker and a ring beside the record.
   *
   * Keyed the same way `takeLocatedOffMap` keys — trimmed. Worth knowing: this
   * class normalises its key three different ways across five methods (raw in
   * `removeLocatedVessel`, `String()` in `isLocatedOnMap`, trimmed here and in
   * the two take-off paths). That predates the extraction and is left alone
   * rather than "tidied": every caller of THIS method passes an already
   * trimmed key, so the choice is provably inert here, while changing the
   * other four would be a behaviour change wearing a cleanup's clothes.
   */
  recordFor(mmsi: string): LocatedVesselRecord | null {
    return this.vessels.get(String(mmsi ?? '').trim())?.record ?? null;
  }

  /**
   * Persist a located vessel — free, per §A5.
   *
   * Best-effort, and the store owns what that MEANS: the marker is already on
   * the map either way, so a failure only leaves the vessel bar reporting the
   * hull as not kept. Both outcomes re-push the panel rows, because `kept` is
   * one of the things a row prints.
   */
  keepVessel(record: LocatedVesselRecord): void {
    this.opts.persist(record).then((kept) => {
      // Null = never attempted; the record keeps whatever it already said and
      // the rows are not re-pushed, exactly as the pre-extraction early return
      // behaved.
      if (kept === null) return;
      record.kept = kept;
      this.pushLocatedState();
    });
  }

  /**
   * Take a located vessel's marker off the map, keeping its saved record.
   *
   * The reversible half of the pair below. A Saved Vessels row is LIST-ONLY
   * until its Locate is clicked, so this simply puts the row back in that state:
   * the vessel stays saved, stays in the panel, and one click draws it again.
   * Nothing is sent to the server, because nothing about the record changed.
   *
   * Kept distinct from `removeLocatedVessel` because the two acts were one act
   * with one ✕ behind it: "clear my map" and "forget this vessel" are not the
   * same decision, and only one of them can be undone.
   */
  takeLocatedOffMap(mmsi: string): void {
    const key = String(mmsi ?? '').trim();
    const entry = this.vessels.get(key);
    if (!entry) return;
    if (entry.marker) this.opts.removeMarker(entry.marker);
    // The card is not bound to the marker, so it does not leave with it.
    this.ctx.popup.closeVesselPopupFor(key);
    this.vessels.delete(key);
    if (this.ctx.selection.activeMmsi() === key && !this.ctx.host.track.hasTrack) {
      this.ctx.selection.deactivateVessel();
    }
    this.pushLocatedState();
  }

  /**
   * Take EVERY located marker off the map in one act — the ON MAP bar's
   * `layers_clear`, and the counterpart of the snapshot and track rows' own.
   *
   * Nothing is deleted: each vessel keeps its Saved Vessels row and can be drawn
   * again from it. Iterated over a copy of the keys because `takeLocatedOffMap`
   * mutates the map it would otherwise be walking.
   */
  takeAllLocatedOffMap(): void {
    for (const mmsi of Array.from(this.vessels.keys())) {
      this.takeLocatedOffMap(mmsi);
    }
  }

  /**
   * Delete a vessel from Saved Vessels — permanently, and off the map with it.
   *
   * The irreversible half: the stored record goes, server-side too, so a reload
   * does not resurrect it. Works for an undrawn row as well (history is
   * list-only until located), where there is no marker to take down, only the
   * record.
   */
  removeLocatedVessel(mmsi: string): void {
    const entry = this.vessels.get(mmsi);
    if (entry) {
      if (entry.marker) this.opts.removeMarker(entry.marker);
      this.ctx.popup.closeVesselPopupFor(mmsi);
      this.vessels.delete(mmsi);
      if (this.ctx.selection.activeMmsi() === mmsi && !this.ctx.host.track.hasTrack) {
        this.ctx.selection.deactivateVessel();
      }
    }
    // Forget on the provider too, so a reload does not resurrect the record.
    // Whether this hull was ever SAVED is the store's own verdict (the entry
    // carries it); whether the provider still holds it is the provider's.
    this.opts.forget(mmsi, !!entry?.record.kept);
    this.pushLocatedState();
  }

  /** Recompute the search-history rows and hand them to their controller. */
  pushLocatedState(): void {
    const rows = Array.from(this.vessels.values()).map(({ record: r }) => ({
      mmsi: r.mmsi,
      name: r.name,
      timeUtc: r.timeUtc,
      stale: isStaleFix(r.timeUtc),
      kept: r.kept,
      query: r.query ?? null,
    }));
    this.ctx.host.locatedVessels.setLocatedVessels(rows);
  }

  /**
   * Forgets every entry WITHOUT touching the map — for the task-switch reset,
   * which runs inside a shared sweep that has already taken every layer off
   * (invariant I1). Located vessels belong to the task that just closed; the
   * next task hydrates its own from its own saved list. The facade's
   * teardown composes this with the layer's own `dropRefs` and the drawer
   * close it owns.
   */
  dropRefs(): void {
    this.vessels.clear();
  }
}
